<template>
  <section v-if="actors" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6">
    <div class="tw-flex items-center justify-between mb-4">
      <h3 class="text-base font-semibold text-gray-900 m-0">Claimed vs unclaimed over time</h3>
    </div>
    <p class="text-sm text-gray-500 mt-0 mb-3">
      Total actors compared with the number that have been claimed, over time.
    </p>

    <div v-if="hasData" class="chart-container">
      <div class="chart-main">
        <SimpleLineChart :data="chartData"/>
      </div>
      <div class="chart-aside">
        <EcosystemInfoCard labelColor="#2E59FA" title="Total actors">
          <span class="text-lg">{{ totalActors }}</span>
        </EcosystemInfoCard>
        <EcosystemInfoCard labelColor="#5CCC61" title="Claimed">
          <span class="text-lg">{{ claimedActors }}</span>
          <span class="text-muted">{{ claimedPct }}% claimed</span>
        </EcosystemInfoCard>
      </div>
    </div>
    <div v-else class="text-sm text-gray-400 py-2">No actor data yet</div>
  </section>
</template>

<script>
  import moment from 'moment'
  import SimpleLineChart from '../../Chart/SimpleLineChart.vue'
  import EcosystemInfoCard from './EcosystemInfoCard.vue'

  export default {
    name: 'HeartbeatClaimsOverTime',
    components: {
      SimpleLineChart,
      EcosystemInfoCard,
    },
    props: {
      actors: Object,
      claims: Object,
    },
    computed: {
      // Per-month new-count maps (default to {} when missing or an error array)
      actorIncrements () {
        return this.sumMaps(this.safeMap('legal_entity_count'), this.safeMap('person_count'))
      },
      claimIncrements () {
        return this.sumMaps(this.safeClaimMap('company_claim_count'), this.safeClaimMap('person_claim_count'))
      },
      months () {
        const keys = new Set([
          ...Object.keys(this.actorIncrements),
          ...Object.keys(this.claimIncrements),
        ])
        return Array.from(keys).sort()
      },
      hasData () {
        return this.months.length > 0
      },
      series () {
        let cumActors = 0
        let cumClaims = 0
        const total = []
        const claimed = []

        this.months.forEach(month => {
          cumActors += this.actorIncrements[month] || 0
          cumClaims += this.claimIncrements[month] || 0
          total.push(cumActors)
          claimed.push(Math.min(cumClaims, cumActors))
        })

        return { total, claimed }
      },
      totalActors () {
        const total = this.series.total
        return total.length ? total[total.length - 1] : 0
      },
      claimedActors () {
        const claimed = this.series.claimed
        return claimed.length ? claimed[claimed.length - 1] : 0
      },
      claimedPct () {
        return this.totalActors > 0 ? Math.round((this.claimedActors / this.totalActors) * 100) : 0
      },
      chartData () {
        return {
          labels: this.months.map(month => moment(month).format('MMM YY')),
          datasets: [
            {
              borderColor: '#2E59FA',
              backgroundColor: '#2E59FA64',
              label: 'Total actors',
              data: this.series.total,
            },
            {
              borderColor: '#5CCC61',
              backgroundColor: '#5CCC6164',
              label: 'Claimed actors',
              data: this.series.claimed,
            },
          ],
        }
      },
    },
    methods: {
      safeMap (key) {
        const value = this.actors && this.actors[key]
        return value && !Array.isArray(value) ? value : {}
      },
      safeClaimMap (key) {
        const value = this.claims && this.claims[key]
        return value && !Array.isArray(value) ? value : {}
      },
      sumMaps (a, b) {
        const result = {}
        Object.keys(a).forEach(key => { result[key] = (result[key] || 0) + (a[key] || 0) })
        Object.keys(b).forEach(key => { result[key] = (result[key] || 0) + (b[key] || 0) })
        return result
      },
    },
  }
</script>
